class VidtoutVideoTabs extends HTMLElement {
  connectedCallback() {
    bindVidtoutExclusiveVideoPlayback(this.closest('.vidtout-videos'));

    this.tabs   = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.copies = [...this.querySelectorAll('[data-panel-copy]')];
    this.stage  = this.querySelector('[data-demo-stage]');
    this.player = this.querySelector('[data-player]');

    if (!this.tabs.length || !this.panels.length) return;

    this._activeIndex = 0;
    this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.activate(i)));
    this.addEventListener('keydown', (e) => this._onKey(e));

    this._measureHeights();
    this._onResize = () => {
      this._measureHeights();
      this._setStageHeight(this._activeIndex);
    };
    window.addEventListener('resize', this._onResize);

    this.activate(0, false);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
  }

  _isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  _aspectForIndex(index) {
    return this.tabs[index]?.dataset.aspect || '16/9';
  }

  _isPortraitAspect(aspect) {
    return aspect.replace(/\s/g, '') === '9/16';
  }

  _normalizeAspect(aspect) {
    const [w, h] = aspect.split('/').map((part) => part.trim());
    return `${w} / ${h}`;
  }

  _measureHeights() {
    if (!this.player || !this.stage || this._isMobile()) return;

    const savedIndex = this._activeIndex ?? 0;
    this.stage.style.height = 'auto';

    this._heights = this.tabs.map((_, i) => {
      const aspect = this._aspectForIndex(i);
      this._applyAspect(aspect, true);
      if (this._isPortraitAspect(aspect)) return null;
      return this.player.getBoundingClientRect().height;
    });

    this._applyAspect(this._aspectForIndex(savedIndex), true);
  }

  _applyAspect(aspect, portraitClassOnStage = true) {
    if (!this.player) return;
    this.player.style.setProperty('--vidtout-video-aspect', this._normalizeAspect(aspect));
    const isPortrait = this._isPortraitAspect(aspect);
    this.player.classList.toggle('vidtout-videos__player--portrait', isPortrait);
    if (portraitClassOnStage && this.stage) {
      this.stage.classList.toggle('is-portrait', isPortrait);
    }
  }

  _setStageHeight(index) {
    if (!this.stage) return;

    /* Portrait + mobile: natural CSS height (same as press M6 card) */
    if (this._isMobile() || this._isPortraitAspect(this._aspectForIndex(index))) {
      this.stage.style.height = '';
      return;
    }

    if (!this._heights?.[index]) return;
    this.stage.style.height = `${this._heights[index]}px`;
  }

  activate(index, animate = true) {
    const prev = this._activeIndex ?? 0;
    const aspect = this._aspectForIndex(index);

    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });

    /* crossfade panels — same pattern as press carousel */
    if (animate && prev !== index) {
      const outgoing = this.panels[prev];
      outgoing?.classList.add('is-leaving');
      outgoing?.classList.remove('is-active');
      outgoing?.querySelector('video')?.pause();
      outgoing?.addEventListener('transitionend', () => outgoing.classList.remove('is-leaving'), { once: true });
    } else {
      this.panels.forEach((p) => p.classList.remove('is-leaving', 'is-active'));
    }

    this.panels.forEach((panel, i) => {
      const on = i === index;
      if (on) {
        panel.classList.add('is-active');
        panel.removeAttribute('inert');
        panel.removeAttribute('aria-hidden');
      } else if (!animate || i !== prev) {
        panel.classList.remove('is-active', 'is-leaving');
        panel.setAttribute('aria-hidden', 'true');
        panel.querySelector('video')?.pause();
        panel.setAttribute('inert', '');
      }
    });

    this.copies.forEach((copy, i) => {
      const on = i === index;
      if (on) {
        copy.hidden = false;
        copy.classList.add('is-active');
        copy.style.animation = 'none';
        void copy.offsetHeight;
        copy.style.animation = '';
      } else {
        copy.classList.remove('is-active');
        copy.hidden = true;
      }
    });

    this._applyAspect(aspect);
    if (!this._isMobile() && !this._isPortraitAspect(aspect)) {
      const measured = this._heights?.[index];
      if (!measured || measured < 40) this._measureHeights();
    }
    this._setStageHeight(index);

    this._activeIndex = index;
  }

  _onKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    if (!e.target.closest('[data-tab]')) return;

    e.preventDefault();
    const n = this.tabs.length;
    let next = this._activeIndex ?? 0;

    if (e.key === 'ArrowRight') next = (next + 1) % n;
    if (e.key === 'ArrowLeft')  next = (next - 1 + n) % n;
    if (e.key === 'Home')       next = 0;
    if (e.key === 'End')        next = n - 1;

    this.activate(next);
    this.tabs[next].focus();
  }
}

if (!customElements.get('vidtout-video-tabs')) {
  customElements.define('vidtout-video-tabs', VidtoutVideoTabs);
}

/* ── Press carousel ── */
class VidtoutPressCarousel extends HTMLElement {
  static MOBILE_MQ = '(max-width: 749px)';

  connectedCallback() {
    bindVidtoutExclusiveVideoPlayback(this.closest('.vidtout-videos'));

    this.stage   = this.querySelector('[data-press-stage]');
    this.track   = this.querySelector('[data-press-track]');
    this.slides  = [...this.querySelectorAll('[data-press-slide]')];
    this.prev    = this.querySelector('[data-press-prev]');
    this.next    = this.querySelector('[data-press-next]');
    this.dotsEl  = this.querySelector('[data-press-dots]');

    this._index = 0;
    this._mq = window.matchMedia(VidtoutPressCarousel.MOBILE_MQ);

    if (this.slides.length > 1) {
      this._buildDots();
      this.prev?.addEventListener('click', () => this.goTo(this._index - 1));
      this.next?.addEventListener('click', () => this.goTo(this._index + 1));
    }

    this._loadDurations();

    this._onModeChange = () => this._applyMode();
    this._mq.addEventListener('change', this._onModeChange);
    this._onResize = () => {
      if (!this._mq.matches) return;
      this._measureHeights();
      this.stage.style.height = `${this._heights[this._index]}px`;
    };
    window.addEventListener('resize', this._onResize);

    if (this.slides.length > 1) this._applyMode();
  }

  disconnectedCallback() {
    this._mq?.removeEventListener('change', this._onModeChange);
    window.removeEventListener('resize', this._onResize);
  }

  _formatDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    const total = Math.round(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  _loadDurations() {
    this.querySelectorAll('.vidtout-videos__press-player video').forEach((video) => {
      const badge = video.parentElement?.querySelector('[data-press-duration]');
      if (!badge) return;

      const showDuration = () => {
        const label = this._formatDuration(video.duration);
        if (!label) return;
        badge.textContent = label;
        badge.hidden = false;
      };

      if (video.readyState >= 1) {
        showDuration();
      } else {
        video.addEventListener('loadedmetadata', showDuration, { once: true });
      }
    });
  }

  _applyMode() {
    const mobile = this._mq.matches;
    this.classList.toggle('is-mobile', mobile);
    this.track.classList.toggle('is-mobile-carousel', mobile);

    if (mobile) {
      this._bindSwipe();
      this._measureHeights();
      this.goTo(this._index, false);
    } else {
      this._unbindSwipe();
      this.stage.style.height = '';
      this.slides.forEach((s) => {
        s.classList.remove('is-active', 'is-leaving', 'is-measuring');
        s.removeAttribute('aria-hidden');
        s.removeAttribute('inert');
      });
    }
  }

  _measureHeights() {
    this._heights = this.slides.map((slide) => {
      slide.classList.remove('is-active', 'is-leaving');
      slide.classList.add('is-measuring');
      const h = slide.offsetHeight;
      slide.classList.remove('is-measuring');
      return h;
    });
  }

  _bindSwipe() {
    this._touchStartX = 0;
    this._onTouchStart = (e) => { this._touchStartX = e.touches[0].clientX; };
    this._onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      if (Math.abs(dx) > 48) this.goTo(this._index + (dx < 0 ? 1 : -1));
    };
    this.stage.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.stage.addEventListener('touchend', this._onTouchEnd, { passive: true });
  }

  _unbindSwipe() {
    if (this._onTouchStart) {
      this.stage.removeEventListener('touchstart', this._onTouchStart);
      this.stage.removeEventListener('touchend', this._onTouchEnd);
    }
  }

  _buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'vidtout-videos__press-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Vidéo ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsEl.appendChild(dot);
    });
    this._dots = [...this.dotsEl.children];
    this._updateDots();
  }

  goTo(index, animate = true) {
    if (!this._mq.matches || this.slides.length < 2) return;

    const n = this.slides.length;
    const next = ((index % n) + n) % n;
    if (next === this._index && animate) return;

    const prev = this._index;
    this._activateMobile(next, prev, animate);
  }

  _activateMobile(next, prev, animate) {
    const outgoing = this.slides[prev];
    const incoming = this.slides[next];

    if (animate && outgoing && prev !== next) {
      outgoing.classList.add('is-leaving');
      outgoing.classList.remove('is-active');
      outgoing.querySelector('video')?.pause();
    } else {
      this.slides.forEach((s) => {
        s.classList.remove('is-leaving', 'is-active');
        s.querySelector('video')?.pause();
      });
    }

    incoming.classList.add('is-active');
    incoming.classList.remove('is-leaving');
    incoming.removeAttribute('inert');
    incoming.removeAttribute('aria-hidden');

    this.slides.forEach((s, i) => {
      if (i !== next) {
        s.setAttribute('aria-hidden', 'true');
        s.setAttribute('inert', '');
        s.querySelector('video')?.pause();
      }
    });

    const targetH = this._heights?.[next] ?? incoming.offsetHeight;
    this.stage.style.height = `${targetH}px`;

    this._index = next;
    this._updateDots();

    if (outgoing && animate) {
      outgoing.addEventListener('transitionend', () => outgoing.classList.remove('is-leaving'), { once: true });
    }
  }

  _updateDots() {
    this._dots?.forEach((dot, i) => {
      const on = i === this._index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }
}

if (!customElements.get('vidtout-press-carousel')) {
  customElements.define('vidtout-press-carousel', VidtoutPressCarousel);
}

/** Pause other showcase videos when one starts playing (demo + press). */
function bindVidtoutExclusiveVideoPlayback(section) {
  if (!section || section.dataset.vidtoutVideoPlaybackBound === 'true') return;

  section.dataset.vidtoutVideoPlaybackBound = 'true';
  section.addEventListener(
    'play',
    (event) => {
      const playing = event.target;
      if (!(playing instanceof HTMLVideoElement)) return;

      section.querySelectorAll('video').forEach((video) => {
        if (video !== playing && !video.paused) video.pause();
      });
    },
    true
  );
}

function initVidtoutVideoShowcasePlayback() {
  document.querySelectorAll('.vidtout-videos').forEach(bindVidtoutExclusiveVideoPlayback);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVidtoutVideoShowcasePlayback);
} else {
  initVidtoutVideoShowcasePlayback();
}
